// Shows time (HH:MM) if the date is "today" in the chosen time zone,
// otherwise shows localized "day + month".
export function formatDayOrTime(
  input: string | Date,
  opts: {
    locale?: string;
    timeZone?: string;
    month?: 'short' | 'long' | 'numeric';
    day?: '2-digit' | 'numeric';
    hour12?: boolean;
  } = {},
): string {
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return '';

  const { locale, timeZone, month = 'short', day = '2-digit', hour12 } = opts;

  // Compare Y-M-D in the target time zone
  const ymd = (d: Date) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);
    const y = parts.find((p) => p.type === 'year')!.value;
    const m = parts.find((p) => p.type === 'month')!.value;
    const dd = parts.find((p) => p.type === 'day')!.value;
    return `${y}-${m}-${dd}`;
  };

  const isToday = ymd(new Date()) === ymd(date);

  if (isToday) {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12,
    }).format(date);
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    day,
    month,
  }).format(date);
}

export function formatDateForShowingFullDate(iso: string, locale: string, opts: { timeZone?: string } = {}) {
  const d = new Date(iso);
  if (isNaN(d as unknown as number)) return '';

  const fmt = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
  });

  const parts = fmt.formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';

  const day = get('day'); // e.g. "27"
  const month = get('month'); // e.g. "августа" / "August" / "agosto"
  const year = get('year'); // e.g. "2025"
  const hour = get('hour'); // "00".."23"
  const minute = get('minute'); // "00".."59"

  return `${day} ${month} ${year}, ${hour}:${minute}`;
}

/** Локальный Date -> "YYYY-MM-DD" (без сдвига по таймзоне). Для дат-«календарных дней». */
export function formatDateToYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** "YYYY-MM-DD" или ISO -> локальный Date на полночь (читаем UTC-компоненты). */
export function parseYMDToLocalDate(dateStr?: string | null): Date | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** "12 апреля 2026" -> "Апреля 2026" для текущего года, иначе просто "2013". */
export function formatMemberSince(dateStr: string, locale: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';

  const year = date.getUTCFullYear();
  const currentYear = new Date().getUTCFullYear();

  if (year !== currentYear) {
    return `${year}`;
  }

  const parts = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(date);
  const month = parts.find((p) => p.type === 'month')?.value ?? '';

  return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`;
}

export function formatDateToDay(dateStr: string): string {
  if (!dateStr) {
    return '';
  }
  const date = new Date(dateStr);

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // months are 0-based
  const year = date.getUTCFullYear();

  return `${day}.${month}.${year}`;
}

export function getLastVisitInfo(
  dateStr: string,
): 'lastVisitToday' | 'lastVisitYesterday' | 'lastVisitThisWeek' | 'lastVisitLongAgo' {
  const now = new Date();
  const date = new Date(dateStr);
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((nowDay.getTime() - dateDay.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'lastVisitToday';
  if (diffDays === 1) return 'lastVisitYesterday';
  if (diffDays <= 7) return 'lastVisitThisWeek';
  return 'lastVisitLongAgo';
}
